/* Relatórios (Painel Administrativo) — histórico de respostas (runs) da IA.
 *
 * Ligada ao real `/api/runs` (filtra por estado) e `/api/runs/:id/reprocess`.
 * O reprocessamento só aparece para runs falhas/expiradas sem sucessora — o
 * mesmo caminho automático, agora com autor. Nebula. */

import React, { useState } from 'react';

import { api } from '../api/cliente';
import { useRecurso } from '../estado/recurso';
import { relativo } from './formato';
import { Aviso, Button, Cabecalho, Nota, Secao, Tag } from './ui';

const TOM = { completed: 'success', failed: 'danger', expired: 'warning' };
const ROTULO = { completed: 'Concluída', failed: 'Falhou', expired: 'Expirou' };

const th = {
  position: 'sticky', top: 0, textAlign: 'left', padding: '12px 20px',
  background: 'var(--surface-card)', font: 'var(--type-label)', fontWeight: 'var(--fw-semibold)',
  color: 'var(--text-muted)', letterSpacing: 'var(--ls-wide)', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap',
};
const td = { padding: '11px 20px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-subtle)' };

export default function Relatorios() {
  const [status, setStatus] = useState('');
  const recurso = useRecurso((sinal) => api.runs({ status: status || undefined }, sinal), [status]);
  const [reprocessando, setReprocessando] = useState(null);
  const itens = recurso.dados?.itens || [];

  async function reprocessar(id) {
    setReprocessando(id);
    try {
      await api.reprocessar(id);
      recurso.recarregar();
    } catch { /* erro fica no estado da run ao recarregar */ } finally {
      setReprocessando(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Cabecalho
        titulo="Relatórios"
        descricao="O histórico das respostas da IA e seu desfecho — o que concluiu, o que falhou."
        acoes={(
          <div style={{ display: 'flex', gap: 4 }}>
            {[['', 'Todas'], ['completed', 'Concluídas'], ['failed', 'Falhas'], ['expired', 'Expiradas']].map(([v, r]) => {
              const ativo = status === v;
              return (
                <button key={r} type="button" onClick={() => setStatus(v)} style={{ padding: '8px 12px', border: '1px solid ' + (ativo ? 'transparent' : 'var(--border-default)'), borderRadius: 'var(--radius-control)', cursor: 'pointer', background: ativo ? 'var(--accent-soft)' : 'transparent', color: ativo ? 'var(--text-brand)' : 'var(--text-secondary)', font: 'var(--type-label)', fontWeight: 'var(--fw-semibold)' }}>{r}</button>
              );
            })}
          </div>
        )}
      />
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 24px 8px' }}>
        <Secao titulo={recurso.carregando ? 'Carregando…' : `${itens.length} ${itens.length === 1 ? 'resposta' : 'respostas'}`}>
          {recurso.erro ? (
            <Aviso titulo="Os relatórios não carregaram" texto={recurso.erro.mensagem} acao={recurso.erro.temporario ? 'Tentar de novo' : null} aoAcao={recurso.recarregar} />
          ) : !recurso.carregando && itens.length === 0 ? (
            <Aviso titulo="Nenhuma resposta neste filtro" texto="Troque o filtro para ver outras." />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)' }}>
              <thead>
                <tr><th style={th}>Empresa</th><th style={th}>Resposta</th><th style={th}>Estado</th><th style={{ ...th, textAlign: 'right' }}>Quando</th><th style={{ ...th, textAlign: 'right' }}>Ação</th></tr>
              </thead>
              <tbody>
                {itens.map((r) => {
                  const podeReprocessar = (r.status === 'failed' || r.status === 'expired') && !r.sucessora;
                  return (
                    <tr key={r.id}>
                      <td style={{ ...td, color: 'var(--text-primary)' }}>{r.assistente || '—'}</td>
                      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-label)' }}>{r.id}</td>
                      <td style={td}><Tag tone={TOM[r.status] || 'neutral'} dot>{ROTULO[r.status] || r.status}</Tag></td>
                      <td className="nb-tab" style={{ ...td, textAlign: 'right', color: 'var(--text-muted)' }}>{relativo(r.em)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        {r.sucessora ? (
                          <span style={{ font: 'var(--type-caption)', color: 'var(--text-placeholder)' }}>já reprocessada</span>
                        ) : podeReprocessar ? (
                          <Button variant="secondary" icon="rotate-cw" style={{ height: 30, padding: '0 10px', fontSize: 'var(--fs-label)' }} onClick={() => reprocessar(r.id)} disabled={reprocessando === r.id}>{reprocessando === r.id ? '…' : 'Reprocessar'}</Button>
                        ) : (
                          <span style={{ font: 'var(--type-caption)', color: 'var(--text-placeholder)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Secao>
        <Nota>Fonte: /api/runs. Reprocessar devolve a run ao orquestrador — o mesmo caminho automático, agora com autor no registro.</Nota>
      </div>
    </div>
  );
}
